import { createFileRoute } from '@tanstack/react-router'
import clsx from 'clsx'
import { add, isFuture } from 'date-fns'
import { useAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { MdCheckCircle, MdCloudUpload, MdError, MdInfo, MdLock } from 'react-icons/md'
import { queryClient, useAssistants, useBotConfig, useDataSet, useDataSources, useIndexModules, useModules, useUpdateBotConfig, useUpdateDataSet } from '~/api'
import { LockOpen } from '~/assets/LockOpen'
import { Spinner } from '~/assets/Spinner'
import { CourseHeading } from '~/components/educator/CourseHeading'
import { CheckLockedOut } from '~/components/educator/data/CheckLockedOut'
import { CourseModuleRow } from '~/components/educator/data/CourseModuleRow'
import { UpdateDatasetModal } from '~/components/educator/data/UpdateDatasetModal'
import { Toggle } from '~/components/shared/Toggle'
import { useStatusUpdateSubscription } from '~/hooks/useStatusUpdateSubscription'
import { isContinuePollingStatus, ModuleItem } from '~/model'
import { selectedResourcesAtom } from '~/state'
import { convertBytesNumberToString, convertBytesStringToNumber } from '~/utils/utils'
import { Alert, Button, Checkbox, cn, Label } from '~ui'

export const Route = createFileRoute('/educator/courses/$courseId/data')({
	component: Data,
})

const MAX_DATA_SET_SIZE = import.meta.env.VITE_MAX_DATA_SET_SIZE ?? ''
const MAX_DATA_SET_SIZE_BYTES = convertBytesStringToNumber(MAX_DATA_SET_SIZE)

function Data() {
	const { courseId } = Route.useParams()
	const { data: modules } = useModules()
	const { indexModules, indexModulesIsPending } = useIndexModules()
	const botConfig = useBotConfig()
	const { mutate: updateBotConfig } = useUpdateBotConfig()
	const { mutateAsync: updateDataSet, isPending: updateDataSetIsPending } = useUpdateDataSet()
	const [selectedResources, setSelectedResources] = useAtom(selectedResourcesAtom)
	const { dataSet } = useDataSet()
	const { dataSources, dataSourcesHasErrors } = useDataSources(true)
	const [showUpdateModal, setShowUpdateModal] = useState(false)
	const assistants = useAssistants()

	useStatusUpdateSubscription()

	const itemsById = useMemo(
		() =>
			modules.reduce<Record<string, ModuleItem | undefined>>((acc, module) => {
				module.items.forEach((item) => {
					acc[item.id] = item
					item.children?.forEach((child) => {
						acc[child.id] = child
					})
				})
				return acc
			}, {}),
		[modules],
	)

	const selectedSize = useMemo(
		() =>
			Object.keys(selectedResources)
				.filter((key) => selectedResources[key])
				.reduce((acc, key) => {
					const item = itemsById[key]
					if (item != null) {
						acc += item.size
					}
					return acc
				}, 0),
		[selectedResources, itemsById],
	)
	const overSize = selectedSize > MAX_DATA_SET_SIZE_BYTES

	useEffect(() => {
		setSelectedResources({})
	}, [setSelectedResources])

	useEffect(() => {
		if (dataSources != null && Object.keys(selectedResources).length == 0) {
			const next: Record<string, boolean> = {}
			dataSources.forEach((dataSource) => {
				if (dataSource.unique_tag != null && dataSource.status === 'READY') {
					next[dataSource.unique_tag] = true
				}
			})
			setSelectedResources(next)
		}
	}, [dataSources, setSelectedResources])

	const allSelected = useMemo(() => {
		return modules.every((module) => module.items.every((item) => selectedResources[item.id] && (item.children == null || item.children.every((child) => selectedResources[child.id]))))
	}, [modules, selectedResources])

	if (botConfig == null) return

	const isUpdating = isContinuePollingStatus(botConfig.status) || updateDataSetIsPending

	const isLocked = botConfig.locked && isFuture(add(new Date(botConfig.locked_at ?? ''), { hours: 24 }))

	async function update() {
		const selected: ModuleItem[] = Object.keys(selectedResources)
			.filter((key) => selectedResources[key])
			.map((key) => {
				const itemToSend = itemsById[key]
				if (itemToSend != null) {
					itemToSend.children = undefined
				}
				return itemToSend
			})
			.filter((item) => item != null)

		await updateDataSet(selected)
		setSelectedResources({})
		void queryClient.invalidateQueries({
			queryKey: ['tutorbot', courseId, 'config'],
		})
		setShowUpdateModal(false)
	}

	function handleUpdateClick() {
		if (assistants.some((assistant) => assistant.isEnabled && assistant.assistantType === 'RAG')) {
			void update()
		} else {
			setShowUpdateModal(true)
		}
	}
	return (
		<>
			<CourseHeading>
				<Button
					onClick={handleUpdateClick}
					disabled={indexModulesIsPending || Object.keys(selectedResources).length === 0 || isUpdating || !botConfig?.is_locked_by_current_user || overSize}
					aria-busy={isUpdating}
				>
					{botConfig?.dataset_id == null ? 'Create' : 'Update'} data set
				</Button>
			</CourseHeading>

			<div className="flex shrink-0 flex-wrap items-start justify-between gap-y-[12px]">
				<div className="flex items-center gap-x-[8px]"></div>
			</div>
			<div className="max-w-text mb-[32px]">
				<h2 className="text-navy-100 font-600 mb-[4px] text-[20px]">Subject dataset</h2>
				<p className="mb-[24px]">
					Aila uses a single dataset per subject. Every app created within that subject shares the same dataset, but each app can be configured to reference only a subset of it or specific LMS
					resources. This section is for creating the full dataset — you can define subsets separately within each app.
				</p>
			</div>
			<CheckLockedOut>
				<div
					className="max-w-text bg-light-blue-25 text-navy-100 mb-[24px] flex items-center gap-[10px] p-[12px]"
					role="alert"
				>
					<div className="bg-light-blue-50 flex size-[32px] shrink-0 items-center justify-center">
						<MdInfo
							className="size-[20px]"
							aria-hidden
						/>
						<div className="sr-only">Info</div>
					</div>
					<div>Updating a dataset will temporarily hide all apps in the live version of Aila until the update is complete.</div>
				</div>

				<div className="max-w-text mb-[32px]">
					<h3 className="text-navy-100 font-600 mb-[4px] text-[18px]">Admin access</h3>
					<p className="mb-[24px]">To make changes to LMS resources, you must first lock access to prevent simultaneous editing.</p>
				</div>

				<section
					aria-labelledby="lock-for-editing"
					className="border-dark-grey-25 max-w-text mt-[12px] mb-[24px] flex justify-between gap-x-[24px] border p-[24px]"
				>
					<div>
						<h3
							id="lock-for-editing"
							className="text-navy-100 text-[20px] leading-[140%] font-[600]"
						>
							Lock to update dataset
						</h3>
						<p className="mt-[4px]">Toggle on to edit LMS resources. While locked, other admins won’t be able to make changes.</p>
						<div className="flex flex-wrap items-center gap-x-[12px]">
							<div className={clsx(isLocked ? 'bg-light-blue-25' : 'bg-light-grey-25', 'text-navy-100 mt-[12px] flex max-w-max items-center gap-x-[4px] rounded-[2px] px-[8px] py-[4px]')}>
								{isLocked ? <MdLock className="size-[16px] shrink-0" /> : <LockOpen className="size-[16px] shrink-0" />}
								<div>Currently {isLocked ? 'locked' : 'unlocked'}</div>
							</div>
						</div>
					</div>
					<div className="flex items-center justify-center">
						<Toggle
							checked={isLocked}
							onChange={(checked) => {
								updateBotConfig({ locked: checked })
							}}
							disabled={isContinuePollingStatus(botConfig?.status)}
							screenReaderLabel="Lock for editing"
						/>
					</div>
				</section>

				<div className="max-w-text mb-[24px]">
					<h3 className="text-navy-100 font-600 mb-[4px] text-[18px]">LMS resources</h3>
					<p className="mb-[24px]">Fetch resources from the LMS and organise your dataset below. We will only index the resources that you select.</p>
				</div>

				<div className="mt-[24px] flex items-end justify-between">
					<button
						type="button"
						onClick={() => {
							void indexModules()
							setSelectedResources({})
						}}
						disabled={!isLocked || indexModulesIsPending || isUpdating}
						className="bg-light-blue-100 text-navy-100 hover:bg-light-blue-50 active:bg-light-blue-75 disabled:bg-dark-blue-50 disabled:text-dark-blue-100 px-[12px] py-[8px] text-[14px] font-[700] disabled:cursor-not-allowed"
						aria-busy={indexModulesIsPending}
					>
						Fetch modules from LMS
					</button>
					{!isUpdating && !indexModulesIsPending && isLocked && dataSet?.status === 'Ready' && (
						<div className="bg-light-green-25 text-navy-100 flex items-center gap-x-[4px] rounded-[2px] px-[8px] py-[4px] text-[14px]">
							<MdCheckCircle className="size-[16px]" />
							Data set live
						</div>
					)}
				</div>
				{indexModulesIsPending && (
					<div
						className="mt-[26px] flex items-center gap-x-[8px]"
						role="status"
					>
						<Spinner
							className="text-navy-100 size-[24px]"
							aria-hidden="true"
						/>
						<div className="text-dark-grey-75 text-[14px]">Fetching current modules, pages and files from LMS</div>
					</div>
				)}
				{isUpdating && (
					<div className="mt-[26px] flex items-center gap-x-[8px]">
						<Spinner className="text-navy-100 size-[24px]" />
						Updating data may take a few minutes. You'll be emailed when it's complete, so feel free to exit this page in the meantime.
					</div>
				)}
				<div className="flex flex-col">
					{!isUpdating && !indexModulesIsPending && dataSourcesHasErrors && isLocked && (
						<div className="items-top max-w-text bg-yellow-25 text-navy-100 my-[12px] flex gap-[10px] p-[12px] first:mt-[24px]">
							<div className="flex size-[32px] shrink-0 items-center justify-center bg-yellow-50">
								<MdError className="size-[22px]" />
							</div>
							<div>
								<p>
									The dataset has been updated successfully, but there were errors. You'll find error details in the modules below. We've excluded the problematic pages and files from the dataset.
								</p>
								<p className="pt-[1rem]">Once the errors are resolved, you can fetch the modules again to update the data set.</p>
							</div>
						</div>
					)}

					{botConfig?.status === 'Error' && (
						<Alert
							variant="error"
							className="mt-[12px]"
						>
							Error: There was an error updating the dataset. Please try again.
							{dataSet?.status == 'Error' && (
								<>
									<br />
									{dataSet.comment}
								</>
							)}
						</Alert>
					)}
				</div>

				{!isUpdating && !indexModulesIsPending && modules.length > 0 && (
					<div>
						<div className="mt-[24px] flex items-center justify-between gap-x-[8px]">
							<div className="flex items-center gap-x-[8px]">
								<Checkbox
									id="select-all"
									checked={allSelected}
									disabled={!isLocked}
									onCheckedChange={(checked) => {
										if (checked === true) {
											setSelectedResources(
												modules.reduce<Record<string, boolean>>((acc, module) => {
													module.items.forEach((item) => {
														acc[item.id] = true
														item.children?.forEach((child) => {
															acc[child.id] = true
														})
													})
													return acc
												}, {}),
											)
										} else {
											setSelectedResources({})
										}
									}}
								/>
								<Label
									htmlFor="select-all"
									className="font-700 m0-0 p-0 text-center text-[14px]"
								>
									Select all
								</Label>
							</div>
							<div className={cn(overSize ? 'bg-light-red-25' : 'bg-light-blue-25', 'text-navy-100 flex items-center rounded-[2px] px-[8px] py-[4px] text-[14px]')}>
								{overSize ? <MdError className="size-[20px]" /> : <MdCloudUpload className="size-[20px]" />}
								<div className="pr-[2px] pl-[4px] leading-[20px]">
									Max data set size: {MAX_DATA_SET_SIZE}.{' '}
									{selectedSize > 0 && (
										<>
											Selected: <span className="font-[700]">{convertBytesNumberToString(selectedSize)}</span>
										</>
									)}
								</div>
							</div>
						</div>
						<section
							className={cn('mt-[24px] flex flex-col gap-y-[12px]', !isLocked && 'pointer-events-none opacity-50')}
							aria-label="LMS Modules"
						>
							{modules.map((module) => (
								<CourseModuleRow
									key={module.id}
									module={module}
								/>
							))}
						</section>
					</div>
				)}

				<UpdateDatasetModal
					open={showUpdateModal}
					onClose={() => {
						setShowUpdateModal(false)
					}}
					onConfirm={() => {
						void update()
						setShowUpdateModal(false)
					}}
				/>
			</CheckLockedOut>
		</>
	)
}
